import { useEffect, useSyncExternalStore } from 'react';
import { anyPending } from './outbox';

/**
 * A new build reaching a page that is already open — the web app's counterpart of the
 * phone app's update notice.
 *
 * The server and the service worker give a new build to anybody who opens the app
 * afresh. An app on a home screen hardly ever is: Android brings back the page that
 * was left, often for days, and a hash-routed app never goes back to the server for a
 * page of its own accord. So the page asks: `/version.json` names the build on the
 * server, and `buildId` is this one's, both written by `vite.config.ts`.
 *
 * Asked when the page comes back into view, every half hour while it stays in view,
 * when the connection returns, and once at the start. A different build is offered
 * with «Обновить» (`UpdateNote` over the tabs, and «Приложение»). It is loaded without asking only when it
 * costs the passenger nothing: when they come back after long enough away that the
 * screen is theirs to read afresh, and nothing on it would be lost — no order half
 * typed, no message on its way out, no words in a box, no ride under way. That is
 * what a cold start would have done anyway, and Android does that to a page left
 * this long often enough.
 */

declare const __BUILD_ID__: string;

/** This bundle's build. Empty under a dev server and in tests, where nothing is asked. */
export const buildId: string = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : '';

/** Away this long, and coming back is as good as opening the app. */
export const AWAY_MS = 10 * 60_000;
const EVERY_MS = 30 * 60_000;
/** Not asked twice inside this, whatever happens in between. */
const NOT_SOONER_MS = 60_000;

/** The build `/version.json` names, or null where it names none. */
export function builtAs(answer: unknown): string | null {
  if (typeof answer !== 'object' || answer === null) return null;
  const build = (answer as { build?: unknown }).build;
  return typeof build === 'string' && build.length > 0 ? build : null;
}

/** What to do with what the server said. */
export function decide(
  mine: string,
  theirs: string | null,
  awayMs: number,
  quiet: boolean,
): 'reload' | 'offer' | 'nothing' {
  // A build is different, not newer: the one on the server is the one to run, a
  // rollback as much as a release.
  if (!theirs || theirs === mine) return 'nothing';
  return awayMs >= AWAY_MS && quiet ? 'reload' : 'offer';
}

let offered = false;
let dismissed = false;
let holds = 0;
const changed = new Set<() => void>();

function publish(): void {
  changed.forEach((heard) => heard());
}

/** Whether a different build is waiting and «Обновить» should say so. */
export function useNewBuild(): { waiting: boolean; offered: boolean } {
  const state = useSyncExternalStore(
    (heard) => {
      changed.add(heard);
      return () => changed.delete(heard);
    },
    () => (offered ? (dismissed ? 'dismissed' : 'offered') : 'none'),
  );
  return { waiting: state !== 'none', offered: state === 'offered' };
}

/** «Не сейчас» on the notice: it stays on «Приложение», and goes from over the screen. */
export function dismissNewBuild(): void {
  dismissed = true;
  publish();
}

export function reloadNow(): void {
  window.location.reload();
}

/**
 * Something on screen that a reload would lose, while `holding` is true: the update
 * waits for the tap.
 */
export function useHoldReload(holding: boolean): void {
  useEffect(() => {
    if (!holding) return;
    holds += 1;
    return () => {
      holds -= 1;
    };
  }, [holding]);
}

function quiet(): boolean {
  if (holds > 0 || anyPending()) return false;
  // A box the passenger was typing in when they left: a phone number, a password.
  const typing = document.activeElement;
  if (typing instanceof HTMLInputElement || typing instanceof HTMLTextAreaElement) return false;
  // Words typed and not sent: a chat's box, «Поддержка», a rating's comment.
  return !Array.from(document.querySelectorAll('textarea')).some((box) => box.value.trim() !== '');
}

/** Starts asking. Only in a real build: there is no `/version.json` to ask otherwise. */
export function startUpdates(): void {
  if (!buildId) return;
  let hiddenAt: number | null = null;
  let askedAt = 0;
  let asking = false;

  const ask = async (awayMs: number) => {
    const now = Date.now();
    if (asking || (now - askedAt < NOT_SOONER_MS && awayMs < AWAY_MS)) return;
    asking = true;
    askedAt = now;
    try {
      // A query of its own each time: a worker installed before this one knew of the
      // file would otherwise keep the first answer for good.
      const response = await fetch(`/version.json?at=${now}`, { cache: 'no-store' });
      if (!response.ok) return;
      const verdict = decide(buildId, builtAs(await response.json()), awayMs, quiet());
      if (verdict === 'nothing') return;
      // The new worker, so that the reload — now or on the tap — is the new build's
      // through and through, `public/` included.
      const registration = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
      await registration?.update().catch(() => {});
      if (verdict === 'reload') {
        reloadNow();
        return;
      }
      if (!offered) {
        offered = true;
        publish();
      }
    } catch {
      // No connection, or an answer that was not JSON. Asked again at the next turn.
    } finally {
      asking = false;
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
    hiddenAt = null;
    void ask(away);
  });
  window.addEventListener('online', () => void ask(0));
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void ask(0);
  }, EVERY_MS);
  void ask(0);
}
